import { useCallback, useRef, useState } from 'react';
import { friendlyError, parseValidationErrors } from '../lib/api';

/**
 * Reusable form validation hook used by every form in the app.
 *
 * Responsibilities:
 *   1. Track per-field error messages (`fieldErrors`) + a global banner (`banner`).
 *   2. Translate backend 422 responses (Pydantic) into Spanish via parseValidationErrors().
 *   3. Translate generic errors (401/403/500/network) via friendlyError().
 *   4. Auto-focus + scroll the first invalid field into view (mobile-friendly).
 *   5. Expose a `field(name)` helper to spread onto inputs:
 *        <input {...field('email')} />
 *      which provides ref, aria-invalid, the CSS hook class, and clears the
 *      field's error whenever the user types.
 *
 * Usage:
 *   const v = useFormValidation();
 *   const submit = async () => {
 *     v.reset();
 *     if (!v.validate({
 *       email: !form.email ? 'Email es obligatorio.' : null,
 *       password: form.password.length < 6 ? 'Contraseña mínima 6 caracteres.' : null,
 *     })) return;
 *     try { await api.login(form); }
 *     catch (err) { v.applyError(err, 'No pudimos iniciar sesión.'); }
 *   };
 *   <input {...v.field('email')} value={form.email} onChange={...} />
 *   <FormError msg={v.fieldErrors.email} />
 *   <FormBanner msg={v.banner} />
 */
export default function useFormValidation() {
  const [fieldErrors, setFieldErrors] = useState({});
  const [banner, setBanner] = useState('');
  const refs = useRef({});

  const setRef = useCallback((name) => (el) => {
    if (el) refs.current[name] = el;
  }, []);

  const focusField = useCallback((name) => {
    setTimeout(() => {
      const el = refs.current[name];
      if (!el) return;
      if (typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      // Pull focus to the inner input for keyboard users
      const focusable = el.matches?.('input, textarea, select')
        ? el
        : el.querySelector?.('input, textarea, select');
      if (focusable?.focus) {
        try { focusable.focus({ preventScroll: true }); } catch { /* ignore */ }
      }
    }, 120);
  }, []);

  const clearField = useCallback((name) => {
    setFieldErrors((fe) => {
      if (!fe[name]) return fe;
      const next = { ...fe };
      delete next[name];
      return next;
    });
  }, []);

  const setFieldError = useCallback((name, msg) => {
    setFieldErrors((fe) => ({ ...fe, [name]: msg }));
  }, []);

  const reset = useCallback(() => {
    setFieldErrors({});
    setBanner('');
  }, []);

  /**
   * Synchronous client-side validation.
   * @param {Record<string,string|null|undefined>} errors
   * @returns {boolean} true if all valid, false if there is at least one error.
   */
  const validate = useCallback((errors) => {
    const filtered = {};
    for (const [k, v] of Object.entries(errors || {})) {
      if (v) filtered[k] = v;
    }
    if (Object.keys(filtered).length === 0) {
      setFieldErrors({});
      setBanner('');
      return true;
    }
    setFieldErrors(filtered);
    const keys = Object.keys(filtered);
    const msg = keys.length === 1
      ? filtered[keys[0]]
      : `Revisa los siguientes campos:\n• ${keys.map((k) => filtered[k]).join('\n• ')}`;
    setBanner(msg);
    focusField(keys[0]);
    return false;
  }, [focusField]);

  /**
   * Translate a thrown error from an API call into per-field + banner state.
   * Returns the first invalid field (if any) so callers can decide on step
   * navigation (e.g. PublishProperty wizard).
   */
  const applyError = useCallback((err, fallback) => {
    const fieldErrs = parseValidationErrors(err);
    if (fieldErrs && fieldErrs.length) {
      const byField = {};
      for (const e of fieldErrs) byField[e.field] = e.message;
      setFieldErrors(byField);
      const msg = fieldErrs.length === 1
        ? fieldErrs[0].message
        : `Revisa los siguientes campos:\n• ${fieldErrs.map((e) => e.message).join('\n• ')}`;
      setBanner(msg);
      focusField(fieldErrs[0].field);
      return fieldErrs[0];
    }
    setFieldErrors({});
    setBanner(friendlyError(err, fallback));
    return null;
  }, [focusField]);

  /**
   * Spread helper for input/textarea/select elements.
   *   <input {...v.field('email')} value={form.email} onChange={...} />
   *
   * NOTE: we intentionally do NOT auto-clear on focus — that would wipe the
   * error message that just appeared after submit + auto-scroll. Callers
   * should call `v.clearField(name)` from their own onChange handler instead.
   */
  const field = useCallback((name) => ({
    ref: setRef(name),
    'aria-invalid': !!fieldErrors[name],
    'data-invalid': fieldErrors[name] ? 'true' : undefined,
  }), [fieldErrors, setRef]);

  /** Convenience: tailwind class to append for a red border on errored inputs. */
  const errorClass = useCallback((name) => (
    fieldErrors[name] ? 'border-rose-500 focus-visible:ring-rose-400' : ''
  ), [fieldErrors]);

  return {
    fieldErrors,
    banner,
    setBanner,
    reset,
    validate,
    applyError,
    field,
    errorClass,
    setFieldError,
    clearField,
    focusField,
  };
}
